// Embed bridge — lets the Chrome extension host the full shop app in an iframe
// (https://gethaist.com/shop) and drive it: a one-time auth/config hand-off at
// first paint, plus a postMessage channel for navigation while the iframe stays
// mounted. The shop app otherwise runs exactly as it does standalone.
//
// Hand-off uses the URL hash (NOT postMessage) for the initial bootstrap so the
// token is present synchronously, before AuthProvider decides logged-in/out —
// no race, no flash of the login screen. The hash is stripped immediately so the
// token doesn't linger in the URL / history.
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setTokens, tokenStorage, refreshTokenStorage, isSessionDead } from '../api/client';
import { setBackendMode, getBackendMode, type BackendMode } from '../../utils/backendConfig';

// Dispatched by setTokens() (api/client) whenever the access token changes —
// login, signup, google, or silent refresh. Lets the embed bridge push the
// fresh token back up to the extension host so its sync engine stays authed.
export const TOKENS_SET_EVENT = 'haist-tokens-set';

const HASH_RE = /[#&]haist_embed=([^&]+)/;
const NONCE_KEY = 'haist_embed_nonce'; // sessionStorage — shared secret for postMessage validation
const EMBEDDED_KEY = 'haist_embedded'; // sessionStorage — set once a bootstrap is applied

interface EmbedPayload {
  token?: string;
  refresh?: string | null;
  backendMode?: BackendMode;
  nonce?: string;
}

const VALID_MODES: BackendMode[] = ['prod', 'dev', 'local'];

// Run synchronously at module load (before AuthProvider mounts). Parses the
// one-time `#haist_embed=<urlencoded base64 json>` handed off by the extension,
// applies token + backend mode, remembers the nonce, marks this frame embedded,
// then strips the hash. No-op (and harmless) on a normal standalone visit.
export function applyEmbedBootstrap(): void {
  try {
    const m = HASH_RE.exec(window.location.hash || '');
    if (!m) return;
    const b64 = decodeURIComponent(m[1]);
    const json = decodeURIComponent(escape(atob(b64))); // base64 → utf-8
    const p = JSON.parse(json) as EmbedPayload;
    // Don't re-seed a token the auth layer just declared dead (circuit breaker) —
    // that's what looped the shop into an infinite reload when the backend's
    // /auth/refresh was down. The latch auto-expires (30s), so it retries later.
    if (p.token && !isSessionDead()) setTokens(p.token, p.refresh ?? null);
    // Only switch when it actually CHANGES — setBackendMode does an unconditional
    // window.location.reload(), and the embed hash carries backendMode on every
    // load, so calling it unguarded reloaded the iframe forever (the flicker).
    // Guarded the same way every manual switcher (BackendSwitcher/ShopLayout) is.
    if (p.backendMode && VALID_MODES.includes(p.backendMode) && p.backendMode !== getBackendMode()) {
      setBackendMode(p.backendMode);
    }
    if (p.nonce) sessionStorage.setItem(NONCE_KEY, p.nonce);
    sessionStorage.setItem(EMBEDDED_KEY, '1');
  } catch {
    /* malformed bootstrap — ignore, app behaves as standalone */
  } finally {
    // Always strip the hash if it carried our payload, even on parse failure.
    try {
      if (HASH_RE.test(window.location.hash || '')) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch {
      /* ignore */
    }
  }
}

export function isEmbedded(): boolean {
  try {
    return sessionStorage.getItem(EMBEDDED_KEY) === '1';
  } catch {
    return false;
  }
}

function embedNonce(): string | null {
  try {
    return sessionStorage.getItem(NONCE_KEY);
  } catch {
    return null;
  }
}

// HAIST→Tek auto-follow (OTA, no content-script). The extension panel pushes the
// live Tek context — `roBase` (the exact "…/shop/{id}" URL prefix) + `roId` (the
// RO Tek currently shows) — via a 'tek-context' message; we cache it here.
let tekFollowCtx: { roBase: string | null; roId: number | null } | null = null;

// Decide where (if anywhere) to send the Tek tab when a conversation linked to
// `convTekRoId` opens. Pure + exported for tests. null = don't navigate:
//   - no context yet / unknown Tek URL, or no Tek link on the conversation, or
//   - Tek is already on that RO (skip → no needless reload, no follow loop).
export function tekFollowUrl(
  ctx: { roBase: string | null; roId: number | null } | null,
  convTekRoId: number | null,
): string | null {
  if (!ctx || !ctx.roBase) return null;
  if (convTekRoId == null) return null;
  if (convTekRoId === ctx.roId) return null;
  return `${ctx.roBase}/repair-order/${convTekRoId}`;
}

// Navigate the whole Tek tab to the conversation's RO. Cross-origin top-navigation
// is permitted because this runs within the transient user activation of the
// conversation click; it full-reloads the Tek tab (and this panel) — the accepted
// trade for keeping the feature OTA (no CRX). No-op when not embedded / guarded out.
export function maybeFollowTekTab(convTekRoId: number | null): void {
  if (!isEmbedded()) return;
  const url = tekFollowUrl(tekFollowCtx, convTekRoId);
  if (!url) return;
  try {
    window.top?.location.assign(url);
  } catch {
    /* blocked (no user activation / sandbox) — silently skip */
  }
}

// Background HAIST↔Tek media sync status, pushed by the extension panel
// (TekObserver) via a 'media-sync' message. 'out' = HAIST photos/videos uploading
// to Tek (per-entry state, keyed by the photo's storage.gethaist.com URL); 'in' =
// Tek photos were just copied into HAIST items `addedItemIds`. 'given-up' = failed
// for good this panel session (the panel stopped retrying).
export type TekMediaState = 'pending' | 'uploading' | 'done' | 'failed' | 'given-up';
export interface TekMediaSyncEntry {
  itemId: number;
  url: string;
  kind: 'photo' | 'video';
  state: TekMediaState;
}
export interface TekMediaSyncStatus {
  roId: number;
  convId: number;
  direction: 'out' | 'in';
  pending: number;
  uploading: number;
  done: number;
  failed: number;
  entries: TekMediaSyncEntry[];
  addedItemIds?: number[];
}

const MEDIA_STATES: TekMediaState[] = ['pending', 'uploading', 'done', 'failed'];
// Latest 'out' status per conversation, so a page that mounts mid-transfer (or
// comes back to a conversation) starts from the current badges, not blank.
const lastOutByConv = new Map<number, TekMediaSyncStatus>();
export function lastTekMediaSync(convId: number): TekMediaSyncStatus | null {
  return lastOutByConv.get(convId) ?? null;
}
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

// Minimal shape check on the posted status (cross-frame data). Malformed → null;
// bad entries are dropped rather than failing the whole status.
function parseMediaSyncStatus(raw: unknown): TekMediaSyncStatus | null {
  const s = raw as Partial<TekMediaSyncStatus> | null;
  if (!s || typeof s !== 'object') return null;
  if (typeof s.convId !== 'number' || (s.direction !== 'out' && s.direction !== 'in')) return null;
  if (!Array.isArray(s.entries)) return null;
  const entries = s.entries
    .filter((e): e is TekMediaSyncEntry & { final?: boolean } =>
      !!e && typeof e.itemId === 'number' && typeof e.url === 'string'
      && (e.kind === 'photo' || e.kind === 'video') && MEDIA_STATES.includes(e.state))
    .map(({ final, ...e }): TekMediaSyncEntry => (e.state === 'failed' && final === true ? { ...e, state: 'given-up' } : e));
  return {
    roId: num(s.roId),
    convId: s.convId,
    direction: s.direction,
    pending: num(s.pending),
    uploading: num(s.uploading),
    done: num(s.done),
    failed: num(s.failed),
    entries,
    addedItemIds: Array.isArray(s.addedItemIds) ? s.addedItemIds.filter((id) => typeof id === 'number') : undefined,
  };
}

// Mounted inside the Router. While embedded, listens for the extension's
// `{type:'haist-embed', action, ...}` messages (validated by the shared nonce)
// and drives react-router. Announces readiness so the host can (re)send the
// conversation for the currently-open Tek RO. No-op when not embedded.
export function EmbedNavListener() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!isEmbedded()) return;
    const ourNonce = embedNonce();

    // Push the current token up to the extension host (panel) so its sync
    // engine (TekObserver) is authenticated with the same session — whether the
    // token came from the host's bootstrap or from a login inside this iframe.
    const postTokenUp = () => {
      const token = tokenStorage.get();
      if (!token) return;
      try {
        window.parent?.postMessage(
          { type: 'haist-embed', action: 'token-up', token, refresh: refreshTokenStorage.get(), nonce: ourNonce },
          '*',
        );
      } catch {
        /* ignore */
      }
    };

    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; action?: string; nonce?: string; conversationId?: number | string; route?: string; token?: string; refresh?: string | null; roBase?: string | null; roId?: number | null; on?: boolean; status?: unknown } | null;
      if (!data || data.type !== 'haist-embed') return;
      // Shared-secret check — the nonce was delivered via the trusted hash bootstrap.
      if (ourNonce && data.nonce !== ourNonce) return;
      switch (data.action) {
        case 'navigate':
          if (data.conversationId != null) navigate(`/shop/conversations/${data.conversationId}`);
          else if (data.route) navigate(data.route);
          break;
        case 'tek-context':
          // Live Tek URL prefix + current RO, for HAIST→Tek follow (see maybeFollowTekTab).
          tekFollowCtx = { roBase: data.roBase ?? null, roId: data.roId ?? null };
          break;
        case 'rec-generating':
          // RO-rec generation start/stop → the "Pulling recommended work…" chat
          // bubble. JobPage listens for this window event.
          window.dispatchEvent(new CustomEvent('haist-embed:rec-generating', {
            detail: { conversationId: typeof data.conversationId === 'number' ? data.conversationId : null, on: !!data.on },
          }));
          break;
        case 'media-sync': {
          // Background HAIST↔Tek media sync → per-photo "Sending to Tek…" badges
          // + an immediate inspection refetch (useTekMediaSync listens).
          const status = parseMediaSyncStatus(data.status);
          if (!status) break;
          if (status.direction === 'out') lastOutByConv.set(status.convId, status);
          window.dispatchEvent(new CustomEvent('haist-embed:media-sync', { detail: status }));
          break;
        }
        case 'token':
          if (data.token) setTokens(data.token, data.refresh ?? null);
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', onMessage);
    window.addEventListener(TOKENS_SET_EVENT, postTokenUp);
    // Tell the host we're ready (no nonce echoed back — the host already knows it
    // and we avoid leaking it to '*').
    try {
      window.parent?.postMessage({ type: 'haist-embed', action: 'ready' }, '*');
    } catch {
      /* ignore */
    }
    // Hand off whatever token we already have (from the host bootstrap, or a
    // prior session) so the host's sync engine is authed from the start.
    postTokenUp();
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener(TOKENS_SET_EVENT, postTokenUp);
    };
  }, [navigate]);

  return null;
}
